import React, {useEffect, useRef, useState} from "react";
import {Localizer} from "@/main/react/Types";
import {GroupDTO} from "@/generated-client";
import {ClientFactory} from "@/main/common/ClientFactory";
import { isInterruptedRequestError } from "@/main/common/ApiUtils";
import { getSsrSnapshotSubtreeHtml } from "@/main/react/ssrSnapshot";

// The server-rendered opening-thought list in discussion.jte that this island replaces on mount.
const SSR_THOUGHTS_SELECTOR = '[data-playwright-id="discussion-thoughts-ssr"]';
import ThoughtInDiscussionListDisplayer, {
    Props as ThoughtInDiscussionListDisplayerProps
} from "@/main/react/list/ThoughtInDiscussionListDisplayer";
import ThoughtTextEditor, {Props as ThoughtTextEditorProps, type EditorHandle} from "@/main/react/editor/thought/ThoughtTextEditorLazy";
import { onThoughtPublished } from "@/main/common/EventRegistry";
import ThoughtInDiscussionCardDisplayer from "@/main/react/thoughts/ThoughtInDiscussionCardDisplayer";
import ThoughtAncestorBreadcrumb from "@/main/react/thoughts/ThoughtAncestorBreadcrumb";
import ThoughtChainDisplayer from "@/main/react/thoughts/ThoughtChainDisplayer";
import DiscussionThoughtListControls from "@/main/react/discussion/DiscussionThoughtListControls";
import { isChainViewRequested } from "@/main/react/thoughts/thoughtChainRoute";
import { PaginatedListStatusText } from "@/main/react/list/PaginatedListState";
import { showThoughtTextEditorDialog } from "@/main/react/editor/thought/ThoughtTextEditorDialog";
import type { DialogController } from "@/main/react/Dialog/Dialog";
import { MetaHandler } from "@/main/common/MetaHandler";
import {InfoBanner} from "@/main/react/component/InfoBanner";
import {shouldShowZeroReplyPrompt} from "@/main/util/replyInvitation";
import type {QuestionStateView} from "@/main/common/QuestionFacetTypes";
import type {PublishThoughtSurface} from "@/main/common/ThoughtPublishTypes";
import logger from "@/lib/logger";
import {
    buildDiscussionOpeningArgumentRequest,
    DiscussionOpeningThoughtOrderingStyle,
    normalizeDiscussionOpeningThoughtOrdering,
    normalizeDiscussionGroupPublicIds,
    findPinnedOpeningThought,
    OpeningThoughtWithGroupMatchesDTO,
    stripSeedCursorsForForeignOrdering,
} from "@/main/react/discussion/OpeningThoughtGroupTypes";
import {INSERTED_ITEM_MOTION_HIGHLIGHT_DURATION_MS} from "@/main/react/component/InsertedItemMotion";

const EMPTY_SELECTED_GROUP_PUBLIC_IDS: string[] = [];

export type BaseProps = {
    localizer: Localizer;
    highlightedOpeningArgumentPublicId?: string | undefined;
} & ThoughtTextEditorProps & ThoughtInDiscussionListDisplayerProps;

export default function DiscussionThoughtsDisplayer(props: BaseProps & {
    items?: OpeningThoughtWithGroupMatchesDTO[]; // optional: component can fetch when not provided
    /**
     * Thoughts the SERVER already rendered into the page, handed over so first paint needs no round
     * trip — and so a failed round trip still has something real to show. Distinct from `items` on
     * purpose:
     *
     * - `items` is authoritative and SUPPRESSES fetching. Supplying the server's list that way would
     *   freeze the list forever, because the fetch effect's `if (items !== undefined) return` also
     *   fires when the viewer changes the ordering or the group filter.
     * - `initialItems` only SEEDS. The requested ordering is still fetched, so the viewer ends up with
     *   what they asked for; the seed just removes the empty frame in front of it.
     *
     * It is consumed once, for the params the server rendered. Changing ordering or filter drops it —
     * showing newest-first thoughts under a "best" label would be worse than showing a loading line.
     *
     * On a TERMINAL fetch failure it is retained and rendered as live cards, replacing the inert
     * `ssrSnapshot` markup restore. That is the AGENTS rule 32 win: the island now renders at least
     * what the server sent, instead of a static copy of it. See
     * `designs/01-now/2026-07-24-discussion-route-hydration-convergence-phase-1-2-implementation-plan.md`.
     */
    initialItems?: OpeningThoughtWithGroupMatchesDTO[];
    /**
     * The ordering `initialItems` is in, as the SERVER declared it. Undefined falls back to
     * `SSR_SEEDED_THOUGHTS_ORDERING` inside the helper below (register F20).
     */
    initialItemsOrdering?: DiscussionOpeningThoughtOrderingStyle;
    discussionPublicId?: string;
    discussionGroupsByPublicId?: Record<string, GroupDTO>;
    selectedGroupPublicIds?: string[];
    onSelectedGroupPublicIdsChange?: (selectedGroupPublicIds: string[]) => void;
    openingArgumentOrdering?: DiscussionOpeningThoughtOrderingStyle;
    /**
     * The list's own filter + ordering row (`DiscussionThoughtListControls`), rendered between the
     * composer and the list. These five props are only what that row needs; supply
     * `onOpeningArgumentOrderingChange` (with `onSelectedGroupPublicIdsChange`) to get the row at all.
     * It lives here rather than in the section tab row so it sits beside the list it controls — see the
     * 2026-07-30 amendment in
     * `designs/04-live/2026-05-15-discussion-opening-argument-sorting-and-lazy-group-controls-design.md`.
     */
    onOpeningArgumentOrderingChange?: (openingArgumentOrdering: DiscussionOpeningThoughtOrderingStyle) => void;
    /**
     * PASSAGE anchors: the loaded list, reported whenever it changes, so the page can draw markers on the
     * post body for the blocks the loaded thoughts are anchored to. Report-only: the page derives a
     * content-keyed summary from it and never writes back into this list (rule 31: no two-way sync).
     */
    onItemsChanged?: (items: OpeningThoughtWithGroupMatchesDTO[]) => void;
    discussionGroups?: GroupDTO[];
    hasAnyDiscussionGroups?: boolean;
    isGroupsLoading?: boolean;
    onEnsureDiscussionGroupsLoaded?: () => void | Promise<void>;
    loggedInUsername?: string;
    isSensitive?: boolean;
    suppressSpoilerGuards?: boolean;
    isArchived?: boolean;
    isDeleted?: boolean;
    onViewConversationClicked?: (conversationPublicId: string, argumentPublicId?: string) => void;
    activeConversationPublicId?: string | null;
    // From a "reply to your argument" notification (?reply=1): auto-open the reply composer on the
    // focused/highlighted argument once. Forwarded only to the highlighted card.
    autoOpenReply?: boolean;
    // From an "asked for your answer" notification's Answer action (?write=1, SV-130): open the
    // top-level thought composer once after mount. Same one-shot shape as `autoOpenReply`, at the
    // post level rather than under a focused argument. Inert on a read-only post.
    autoOpenWriteThought?: boolean;
    // From the empty-room hand-off card's CTA (`?from=handoff`, D5/A3): the page consumed the one-shot and
    // hands the value here so BOTH top-level composers (inline and dialog) send `surface: "handoff"` on
    // the publish request. Unset means no field is sent. Never forwarded to nested-reply composers: a
    // hand-off candidate has zero replies, so the hand-off reply is by construction a top-level thought.
    publishSurface?: PublishThoughtSurface;
    // ISO timestamp of the discussion itself, used only to age-gate the zero-reply composer prompt
    // (src/main/util/replyInvitation.ts). Absent means "don't show it", which is the safe direction.
    discussionCreatedAt?: string;
    /**
     * QUESTION facet. `isQuestion` reframes this whole section — the composer placeholder, the empty and
     * end states, and the write CTA — while `onQuestionStateChanged` carries the `QuestionStateView`
     * returned by an accept/unaccept back to the page, which owns the title badges.
     */
    isQuestion?: boolean;
    /**
     * The POST author's username, for the "This changed {0}'s mind" badge on a marked thought.
     *
     * Threaded rather than re-derived: only the post author can mark, so the badge always names them,
     * and the alternative (reading the marked thought's own author) would name the wrong person -
     * the recipient of the credit rather than the person whose mind moved.
     */
    discussionAuthorUsername?: string;
    onQuestionStateChanged?: (view: QuestionStateView) => void;
    /** Round 37 `2a` (FI-583): the viewer's previous visit, forwarded to the list for its divider. */
    viewerLastVisitedAt?: string | null;
}) {
    const {
        localizer,
        items,
        initialItems,
    initialItemsOrdering,
        loggedInUsername,
        discussionPublicId,
        profileImageUrl,
        openingArgumentPublicId,
        initialViewerDiscussionAgreement,
        agreementAgreementTitle,
        disagreementAgreementTitle,
        isUserLoggedIn,
        discussionSlug,
        discussionTitle,
        isSensitive = false,
        suppressSpoilerGuards = false,
        isArchived = false,
        isDeleted = false,
        onViewConversationClicked,
        activeConversationPublicId,
        discussionGroupsByPublicId = {},
        selectedGroupPublicIds,
        onSelectedGroupPublicIdsChange,
        openingArgumentOrdering,
        onOpeningArgumentOrderingChange,
        onItemsChanged,
        discussionGroups,
        hasAnyDiscussionGroups = false,
        isGroupsLoading = false,
        onEnsureDiscussionGroupsLoaded,
        autoOpenReply = false,
        autoOpenWriteThought = false,
        publishSurface,
        discussionCreatedAt,
        isQuestion = false,
        discussionAuthorUsername,
        onQuestionStateChanged,
        viewerLastVisitedAt,
    } = props;
    const resolvedSelectedGroupPublicIds = selectedGroupPublicIds ?? EMPTY_SELECTED_GROUP_PUBLIC_IDS;
    const isReadOnly = isArchived || isDeleted;
    // Linear "Read as conversation" chain: the focused-argument permalink carries ?view=chain. When set
    // (and an anchor is present), render the linear ThoughtChainDisplayer instead of the tree/card. The
    // anchor is the server-bootstrapped openingArgumentPublicId, so this is a full-load flag (read once).
    const isChainView = React.useMemo(() => isChainViewRequested(), []);
    const normalizedSelectedGroupPublicIds = React.useMemo(
        () => normalizeDiscussionGroupPublicIds(resolvedSelectedGroupPublicIds),
        [resolvedSelectedGroupPublicIds],
    );
    const selectedGroupPublicIdsKey = React.useMemo(
        () => normalizedSelectedGroupPublicIds.join(","),
        [normalizedSelectedGroupPublicIds],
    );
    const normalizedOpeningArgumentOrdering = React.useMemo(
        () => normalizeDiscussionOpeningThoughtOrdering(openingArgumentOrdering),
        [openingArgumentOrdering],
    );
    const argumentListRequest = React.useMemo(
        () => buildDiscussionOpeningArgumentRequest(normalizedSelectedGroupPublicIds, normalizedOpeningArgumentOrdering),
        [normalizedOpeningArgumentOrdering, normalizedSelectedGroupPublicIds],
    );

    // The server renders its thoughts in ONE ordering (`new`, pinned for SEO). When the viewer asked
    // for a different one, the rows are still worth showing immediately — but their cursors describe
    // the server's ordering and would 400 on "load more". Keep the rows, drop the cursors.
    const seedForRequestedOrdering = React.useMemo(
        () => stripSeedCursorsForForeignOrdering(
            initialItems,
            normalizedOpeningArgumentOrdering,
            initialItemsOrdering,
        ),
        [initialItems, initialItemsOrdering, normalizedOpeningArgumentOrdering],
    );

    // `initialItems` seeds the first paint only. `pendingSeedRef` holds it until the reset effect
    // applies it, then clears it, so a later ordering/filter change cannot re-apply a seed that
    // described the ORIGINAL params. `activeSeedRef` remembers what was applied, so the fetch's
    // failure branch can tell "we already have real content" from "we have nothing".
    const pendingSeedRef = React.useRef<OpeningThoughtWithGroupMatchesDTO[] | undefined>(seedForRequestedOrdering);
    const activeSeedRef = React.useRef<OpeningThoughtWithGroupMatchesDTO[] | undefined>(items ?? seedForRequestedOrdering);

    const [localItems, setLocalItems] = useState<OpeningThoughtWithGroupMatchesDTO[]>(items ?? initialItems ?? []);
    const onItemsChangedRef = useRef(onItemsChanged);
    onItemsChangedRef.current = onItemsChanged;
    useEffect(() => {
        onItemsChangedRef.current?.(localItems);
    }, [localItems]);
    const [isInitialItemsLoading, setIsInitialItemsLoading] = useState((items ?? initialItems) === undefined);
    const [shouldUseListManagedInitialFetch, setShouldUseListManagedInitialFetch] = useState(false);
    const [newlyPublishedOpeningArgumentPublicIds, setNewlyPublishedOpeningArgumentPublicIds] = useState<string[]>([]);
    // Server-rendered thought markup to fall back to when the initial fetch fails terminally, so the
    // island never leaves the page with less content than the HTML it replaced. See ssrSnapshot.ts.
    const [ssrFallbackHtml, setSsrFallbackHtml] = useState<string | null>(null);
    const argumentEditorDialogControllerRef = React.useRef<DialogController | null>(null);
    // The inline top-level composer, so a stance chip in the empty list can start IT (round 25 `D41`).
    const openingComposerRef = React.useRef<EditorHandle | null>(null);
    const openingArgumentInsertionTimeoutsRef = React.useRef<Record<string, number>>({});
    const openingArgumentsRequestVersionRef = React.useRef(0);

    const beginOpeningArgumentsRequest = React.useCallback(() => {
        openingArgumentsRequestVersionRef.current += 1;
        return openingArgumentsRequestVersionRef.current;
    }, []);

    const isLatestOpeningArgumentsRequest = React.useCallback((requestVersion: number) => {
        return openingArgumentsRequestVersionRef.current === requestVersion;
    }, []);

    // Carries the just-published comment across an ordering SNAP (see the publish subscriber below).
    // Snapping re-derives argumentListRequest, so the list refetches through its own effect rather than
    // through refreshOpeningArgumentsFromServer -- which is where the highlight would otherwise have been
    // scheduled. A ref rather than state: it must not itself cause a render.
    const pendingPublishHighlightRef = React.useRef<string | null>(null);

    const scheduleOpeningArgumentInsertionHighlight = React.useCallback((openingArgumentPublicId: string) => {
        if (!openingArgumentPublicId) {
            return;
        }

        setNewlyPublishedOpeningArgumentPublicIds((prev) => prev.includes(openingArgumentPublicId)
            ? prev
            : [openingArgumentPublicId, ...prev],
        );

        const existingTimeout = openingArgumentInsertionTimeoutsRef.current[openingArgumentPublicId];
        if (existingTimeout != null) {
            window.clearTimeout(existingTimeout);
        }

        openingArgumentInsertionTimeoutsRef.current[openingArgumentPublicId] = window.setTimeout(() => {
            setNewlyPublishedOpeningArgumentPublicIds((prev) => prev.filter((id) => id !== openingArgumentPublicId));
            delete openingArgumentInsertionTimeoutsRef.current[openingArgumentPublicId];
        }, INSERTED_ITEM_MOTION_HIGHLIGHT_DURATION_MS);
    }, []);

    const refreshOpeningArgumentsFromServer = React.useCallback(async (highlightOpeningArgumentPublicId?: string) => {
        if (!discussionPublicId) {
            return;
        }

        const requestVersion = beginOpeningArgumentsRequest();

        try {
            const raw = await ClientFactory.listDiscussionOpeningThoughts<OpeningThoughtWithGroupMatchesDTO[]>({
                discussionPublicId,
                groupPublicIds: argumentListRequest.groupPublicIds,
                openingArgumentOrdering: argumentListRequest.openingArgumentOrdering,
            });
            if (!isLatestOpeningArgumentsRequest(requestVersion)) {
                return;
            }
            const refreshedItems: OpeningThoughtWithGroupMatchesDTO[] = (raw.data as OpeningThoughtWithGroupMatchesDTO[]).map((item) => ({ ...item }));
            setLocalItems(refreshedItems);
            setIsInitialItemsLoading(false);
            setShouldUseListManagedInitialFetch(false);

            if (highlightOpeningArgumentPublicId && refreshedItems.some((item) => item?.openingArgument?.publicId === highlightOpeningArgumentPublicId)) {
                scheduleOpeningArgumentInsertionHighlight(highlightOpeningArgumentPublicId);
            }
        } catch (err) {
            if (!isLatestOpeningArgumentsRequest(requestVersion)) {
                return;
            }
            // See the initial-fetch catch below: an aborted request is not a failure.
            if (isInterruptedRequestError(err)) {
                return;
            }
            logger.error("[DiscussionThoughtsDisplayer] failed to refresh items after publish:", err);

            if (items === undefined && isInitialItemsLoading) {
                setLocalItems([]);
                setShouldUseListManagedInitialFetch(true);
                setIsInitialItemsLoading(false);
            }
        }
    }, [argumentListRequest.groupPublicIds, argumentListRequest.openingArgumentOrdering, beginOpeningArgumentsRequest, discussionPublicId, isInitialItemsLoading, isLatestOpeningArgumentsRequest, items, scheduleOpeningArgumentInsertionHighlight]);

    useEffect(() => {
        openingArgumentsRequestVersionRef.current += 1;
        // Consume the seed at most once. On the mount run this applies the server's thoughts; on any
        // later run (ordering or filter changed) the ref is already cleared, so we fall back to the
        // normal loading path rather than re-showing a list that no longer matches the request.
        const seed = items ?? pendingSeedRef.current;
        pendingSeedRef.current = undefined;
        activeSeedRef.current = seed;
        setLocalItems(seed ?? []);
        setIsInitialItemsLoading(seed === undefined);
        setShouldUseListManagedInitialFetch(false);
        setNewlyPublishedOpeningArgumentPublicIds([]);
    }, [items, normalizedOpeningArgumentOrdering, selectedGroupPublicIdsKey]);

    // If parent didn't provide items, fetch them
    useEffect(() => {
        if (items !== undefined) return;
        if (!discussionPublicId) return;

        let mounted = true;
        const requestVersion = beginOpeningArgumentsRequest();
        // Only announce loading when there is nothing to show. With server-inlined thoughts already on
        // screen this fetch is a background upgrade to the requested ordering, and replacing real
        // content with a loading line would be a visible step BACKWARDS from the HTML we shipped.
        setIsInitialItemsLoading(activeSeedRef.current === undefined);
        setShouldUseListManagedInitialFetch(false);

        (async () => {
            try {
                const raw = await ClientFactory.listDiscussionOpeningThoughts<OpeningThoughtWithGroupMatchesDTO[]>({
                    discussionPublicId,
                    groupPublicIds: argumentListRequest.groupPublicIds,
                    openingArgumentOrdering: argumentListRequest.openingArgumentOrdering,
                });
                const res: OpeningThoughtWithGroupMatchesDTO[] = raw.data as OpeningThoughtWithGroupMatchesDTO[];
                if (!mounted || !isLatestOpeningArgumentsRequest(requestVersion)) return;
                setLocalItems((res).map(r => ({...r})));
                setShouldUseListManagedInitialFetch(false);
                // Live data won — drop any SSR fallback from a previous failed attempt.
                setSsrFallbackHtml(null);
            } catch (e) {
                if (!mounted || !isLatestOpeningArgumentsRequest(requestVersion)) return;
                // The server handed us its thoughts inline, so we already have real content on screen.
                // Handing the list back to its own fetch path would throw that away and show a loading
                // line (or an empty list) instead — the exact subtraction AGENTS rule 32 forbids.
                const seededItems = activeSeedRef.current;
                const hasSeededContent = seededItems !== undefined && seededItems.length > 0;

                // Aborted (navigated away / unmounted / crawler bailed mid-render) is not a failure —
                // it was the single largest source of prod FRONTEND ERROR volume before 2026-07-24.
                // Still hand the list back to its own fetch path so a live viewer isn't left empty.
                if (isInterruptedRequestError(e)) {
                    setShouldUseListManagedInitialFetch(!hasSeededContent);
                    return;
                }
                logger.error("[DiscussionThoughtsDisplayer] failed to fetch items:", e);
                if (hasSeededContent) {
                    // Best case: keep the server-inlined thoughts as LIVE cards. Strictly better than
                    // the snapshot restore below, which is readable but inert — these vote and reply.
                    setShouldUseListManagedInitialFetch(false);
                    return;
                }
                // Terminal failure with nothing inlined (a degraded server render deliberately omits
                // the payload). Rendering an empty list here would leave the page showing LESS than
                // the server-rendered thought list this island replaced on mount — which is how the
                // 2026-07-24 bingbot 403s turned into crawlers indexing empty discussions. Restore that
                // markup instead. It is inert (no voting/replying), which is the accepted trade: static
                // content beats no content. See ssrSnapshot.ts + the audit's finding 3.
                setSsrFallbackHtml(getSsrSnapshotSubtreeHtml("DiscussionPage", SSR_THOUGHTS_SELECTOR));
                setShouldUseListManagedInitialFetch(true);
                setLocalItems([]);
            } finally {
                if (mounted && isLatestOpeningArgumentsRequest(requestVersion)) {
                    setIsInitialItemsLoading(false);
                }
            }
        })();
        return () => {
            mounted = false;
        };
    }, [argumentListRequest, beginOpeningArgumentsRequest, discussionPublicId, isLatestOpeningArgumentsRequest, items, normalizedOpeningArgumentOrdering, selectedGroupPublicIdsKey]);

    const [highlightedOpeningArgument, setHighlightedOpeningArgument] = useState<OpeningThoughtWithGroupMatchesDTO | undefined>();

    // Global event listener: add newly published opening arguments to the top
    useEffect(() => {
        return () => {
            for (const timeoutId of Object.values(openingArgumentInsertionTimeoutsRef.current)) {
                window.clearTimeout(timeoutId);
            }
            openingArgumentInsertionTimeoutsRef.current = {};
        };
    }, []);

    useEffect(() => {
        return onThoughtPublished((published) => {
            try {
                if (!published) return;
                const publishedDiscussionPublicId = (published as typeof published & { discussionPublicId?: string | null }).discussionPublicId?.trim() || undefined;

                // Only consider opening arguments (no conversationPublicId)
                if (!published.publicId || published.conversationPublicId) return;
                if (discussionPublicId && publishedDiscussionPublicId && publishedDiscussionPublicId !== discussionPublicId) return;
                const wrapped: OpeningThoughtWithGroupMatchesDTO = {
                    openingArgument: published,
                    conversationsMap: {},
                } as any;

                // The optimistic prepend is only safe while NOTHING is pinned. With a pin present it
                // would insert the new thought ABOVE the pinned one — the single position the pin is
                // supposed to own — because "newest first" is exactly what the pin overrides. Every
                // other case in this handler already falls through to the refetch; a pinned list is now
                // one of them. Reimplementing "pinned first" in the client's insert logic is how the
                // two orderings drift apart: the server owns the ordering.
                const listHoldsAPin = findPinnedOpeningThought(localItems) !== undefined;
                if (normalizedSelectedGroupPublicIds.length === 0
                    && normalizedOpeningArgumentOrdering === "new"
                    && !listHoldsAPin) {
                    setLocalItems(prev => [wrapped, ...prev]);
                    scheduleOpeningArgumentInsertionHighlight(published.publicId);
                    return;
                }

                /*
                 * You publish a comment and it is nowhere on the screen (2026-09-01, tier-1 finding 11).
                 *
                 * The optimistic prepend above only fires on `new`. The DEFAULT ordering is `best`, a
                 * Wilson lower bound, so a brand-new comment with no votes is not on page one -- and
                 * refreshOpeningArgumentsFromServer's own highlight is guarded on the item coming back,
                 * so it correctly does nothing and the publish leaves no trace at all. The member is left
                 * unable to tell success from failure.
                 *
                 * So: snap the ordering to `new` for the AUTHOR of the comment, once, on publish.
                 *
                 * - Per-member. Only the person who just published gets moved; a reader watching the same
                 *   discussion keeps the ordering they chose.
                 * - Through `onOpeningArgumentOrderingChange`, which is DiscussionPage's setRouteState
                 *   handler. Never a raw history write (rule 33) -- and it PUSHES, because the ordering
                 *   the member was on is an address they navigated to, so Back must return to it. None of
                 *   the four HistoryReplaceReasons describes this, and that is the right answer rather
                 *   than a reason to add a fifth.
                 * - Return rather than also refetching: the snap changes argumentListRequest, and the
                 *   list's own effect fetches from it. Doing both would fire two requests for one publish.
                 */
                const publishedByTheViewer = Boolean(published.username)
                    && Boolean(loggedInUsername)
                    && published.username === loggedInUsername;
                if (publishedByTheViewer
                    && normalizedOpeningArgumentOrdering !== "new"
                    && onOpeningArgumentOrderingChange) {
                    pendingPublishHighlightRef.current = published.publicId;
                    onOpeningArgumentOrderingChange("new");
                    return;
                }

                void refreshOpeningArgumentsFromServer(published.publicId);
            } catch (err) {
                logger.error("[DiscussionThoughtsDisplayer] ThoughtPublishedEvent handler error", err);
            }
        });
    }, [discussionPublicId, loggedInUsername, localItems, normalizedOpeningArgumentOrdering, normalizedSelectedGroupPublicIds.length, onOpeningArgumentOrderingChange, refreshOpeningArgumentsFromServer, scheduleOpeningArgumentInsertionHighlight]);

    // The other half of the snap: highlight the comment once the re-ordered page actually contains it.
    // Guarded on presence rather than on the fetch settling, so a comment that still does not come back
    // (a filter the snap cannot fix) simply never highlights instead of flashing an empty treatment.
    React.useEffect(() => {
        const pending = pendingPublishHighlightRef.current;
        if (!pending) {
            return;
        }
        if (localItems.some((item) => item?.openingArgument?.publicId === pending)) {
            pendingPublishHighlightRef.current = null;
            scheduleOpeningArgumentInsertionHighlight(pending);
        }
    }, [localItems, scheduleOpeningArgumentInsertionHighlight]);

    // After a pin or unpin the list must RE-SORT, and the server owns that ordering under all seven
    // sortings — so re-fetch page 1 rather than moving the card locally. Same call the publish path
    // already uses; no highlight id, because nothing was inserted.
    const handlePinChanged = React.useCallback(() => {
        void refreshOpeningArgumentsFromServer();
    }, [refreshOpeningArgumentsFromServer]);

    // Accepting or unaccepting an answer floats it to the top under ALL SEVEN orderings and the server
    // owns that ordering — the identical argument the pin path makes above — so refetch page 1 rather
    // than re-sorting locally. The state view also goes up to the page, which owns the title badges.
    const handleAcceptChanged = React.useCallback((view: QuestionStateView) => {
        onQuestionStateChanged?.(view);
        void refreshOpeningArgumentsFromServer();
    }, [onQuestionStateChanged, refreshOpeningArgumentsFromServer]);


    useEffect(() => {
        if (!openingArgumentPublicId) return;
        let mounted = true;
        (async () => {
            try {
                const api = ClientFactory.getAjaxThoughtRestControllerApi();
                const raw = await (api).thoughtOpening(openingArgumentPublicId);
                const res: OpeningThoughtWithGroupMatchesDTO = raw.data as OpeningThoughtWithGroupMatchesDTO;
                if (!mounted) return;
                if (highlightedOpeningArgument) return;
                setHighlightedOpeningArgument(res);
            } catch (e) {
                logger.error("[DiscussionOpeningDisplayer] failed to fetch opening argument:", e);
            }
        })();
        return () => { mounted = false; };
    }, [openingArgumentPublicId]);

    // Build the exclude list for the list displayer. Use highlightedOpeningArgument if available, otherwise fall back to openingArgumentPublicId prop.
    const excludeOpeningArgumentPublicIds: string[] = [ ];
    if (openingArgumentPublicId) {
        excludeOpeningArgumentPublicIds.push(openingArgumentPublicId);
    }

    const handleWriteArgumentClicked = React.useCallback(() => {
        if (isReadOnly) {
            return;
        }

        // SV-47: no pre-write wall. The composer itself walls at Publish and flushes the
        // draft first (ThoughtTextEditor), so an anonymous reader may write before signing in.

        if (argumentEditorDialogControllerRef.current) {
            argumentEditorDialogControllerRef.current.setOpen(true);
            return;
        }

        argumentEditorDialogControllerRef.current = showThoughtTextEditorDialog({
            localizer,
            autoFocus: true,
            isUserLoggedIn: MetaHandler.isUserLoggedIn(),
            loggedInUsername: loggedInUsername ?? MetaHandler.getLoggedInUsername() ?? undefined,
            profileImageUrl,
            openingArgumentPublicId: undefined,
            conversationPublicId: undefined,
            discussionPublicId,
            agreementAgreementTitle,
            disagreementAgreementTitle,
            // Shared with DiscussionButtons.openArgumentEditor on purpose: both open the SAME composer
            // (a new top-level thought in this discussion), and only one of them can be open at a time.
            // Distinct from "discussion-opening-thought-editor", which is the inline composer already on
            // the page, and from "discussion-inline-reply-editor", which replies to one comment.
            playwrightIdPrefix: "discussion-write-thought-dialog",
            // Round 25 `D41`: the same top-level composer as the inline one, so the same stance chips.
            showStancePrompts: !isQuestion,
            ...(publishSurface ? { publishSurface } : {}),
        });
    }, [agreementAgreementTitle, disagreementAgreementTitle, discussionPublicId, isQuestion, isReadOnly, isUserLoggedIn, localizer, loggedInUsername, profileImageUrl, publishSurface]);

    // A stance chip in the empty list (round 25 `D41`) starts the INLINE composer, which sits directly
    // above the list on every writable post: it takes the prefill and is brought into view with the
    // caret after it. Until that lazy composer has mounted there is no handle, and the chip falls back
    // to the write-thought dialog rather than doing nothing (the dialog carries the chips itself).
    const handleStancePromptChosen = React.useCallback((prefill: string) => {
        if (isReadOnly) {
            return;
        }
        const composer = openingComposerRef.current;
        if (composer) {
            composer.startWithStance(prefill);
            return;
        }
        handleWriteArgumentClicked();
    }, [handleWriteArgumentClicked, isReadOnly]);

    // SV-130 Answer action: open the top-level composer once, after mount, and never again for this
    // instance however the flag re-renders. Read-only posts are refused inside the handler itself.
    const autoOpenWriteThoughtFiredRef = React.useRef(false);
    useEffect(() => {
        if (!autoOpenWriteThought || autoOpenWriteThoughtFiredRef.current) {
            return;
        }
        autoOpenWriteThoughtFiredRef.current = true;
        handleWriteArgumentClicked();
    }, [autoOpenWriteThought, handleWriteArgumentClicked]);

    useEffect(() => {
        return () => {
            try {
                argumentEditorDialogControllerRef.current?.cleanup();
            } catch {
                // ignore modal cleanup issues
            }
            argumentEditorDialogControllerRef.current = null;
        };
    }, []);

    useEffect(() => {
        if (!argumentEditorDialogControllerRef.current) {
            return;
        }

        try {
            argumentEditorDialogControllerRef.current.cleanup();
        } catch {
            // ignore modal cleanup issues
        }
        argumentEditorDialogControllerRef.current = null;
    }, [agreementAgreementTitle, disagreementAgreementTitle, discussionPublicId, localizer, loggedInUsername, profileImageUrl]);

    const shouldShowInitialItemsLoading = items === undefined && isInitialItemsLoading;
    const initialListItems = shouldUseListManagedInitialFetch ? undefined : localItems;

    // Focused-comment ("Continue this thread") view: when the page is opened on a NESTED reply's
    // permalink, render it as an isolated thread — its ancestor breadcrumb above, its own subtree
    // below — and suppress both the discussion's other top-level arguments and the "start a new
    // top-level argument" composer, which are out of place inside a sub-thread. A focused TOP-LEVEL
    // argument (isOpening) keeps the prior behaviour (highlight + full list). isOpening === false is
    // the tree-model signal for parent_argument_id IS NOT NULL.
    // Linear chain view: render only the ThoughtChainDisplayer (it owns its own back link + ancestor
    // context + downward spine). Suppresses the sibling list + top-level composer, like a focused reply.
    if (isChainView && openingArgumentPublicId) {
        return (
            <div className="mb-20 w-full min-w-0 max-w-250">
                <ThoughtChainDisplayer
                    anchorArgumentPublicId={openingArgumentPublicId}
                    common={{
                        discussionPublicId: discussionPublicId!,
                        discussionSlug: discussionSlug || "",
                        discussionTitle,
                        localizer,
                        loggedInUsername,
                        agreementAgreementTitle,
                        disagreementAgreementTitle,
                        isSensitive,
                        suppressSpoilerGuards,
                    }}
                />
            </div>
        );
    }

    // Reply invitation (T2, shipped to every discussion 2026-09-12, SV-46): on a fresh, still-unanswered
    // post the composer says so, because "yours would be the first" is a uniqueness cue and uniqueness is
    // what the MovieLens field experiments moved. Held back while the list is still resolving — otherwise
    // a post that does have replies would flash the claim that it has none.
    const showZeroReplyPrompt = !isInitialItemsLoading
        && shouldShowZeroReplyPrompt(localItems.length, discussionCreatedAt);
    // U4 / C61 (`DISCUSSION-112`, S7 2026-09-28): the composer owns the zero-reply prompt, and it is never
    // addressed to the author. "Yours would be the first" asks a reader to answer; the author of the post is
    // told instead where the replies will land.
    const viewerIsDiscussionAuthor = Boolean(
        loggedInUsername && discussionAuthorUsername && loggedInUsername === discussionAuthorUsername,
    );

    const focusedArgument = highlightedOpeningArgument?.openingArgument;
    const isFocusedNestedReply = Boolean(openingArgumentPublicId) && focusedArgument?.isOpening === false;
    // While a focused argument is still resolving we don't yet know if it is nested, so hold the
    // sibling list back rather than flash it and then hide it.
    const isResolvingFocusedArgument = Boolean(openingArgumentPublicId) && !highlightedOpeningArgument;

    // The list's filter + ordering row, shown only when there is a list below it to act on: a focused
    // nested reply renders one isolated thread and no siblings, so ordering them would mean nothing.
    const shouldShowListControls = Boolean(onSelectedGroupPublicIdsChange)
        && Boolean(onOpeningArgumentOrderingChange)
        && !isFocusedNestedReply;

    const listControls = shouldShowListControls ? (
        <DiscussionThoughtListControls
            localizer={localizer}
            discussionGroups={discussionGroups}
            hasAnyDiscussionGroups={hasAnyDiscussionGroups}
            selectedGroupPublicIds={normalizedSelectedGroupPublicIds}
            onSelectedGroupPublicIdsChange={onSelectedGroupPublicIdsChange!}
            openingArgumentOrdering={normalizedOpeningArgumentOrdering}
            onOpeningArgumentOrderingChange={onOpeningArgumentOrderingChange!}
            isGroupsLoading={isGroupsLoading}
            onEnsureDiscussionGroupsLoaded={onEnsureDiscussionGroupsLoaded}
        />
    ) : null;
    // On a permalink (`DISCUSSION-111`), keyed on the ROUTE as well as the resolved card so the row does
    // not render above and then jump below while the anchored thought is still resolving.
    const listControlsFollowAnchoredCard = Boolean(openingArgumentPublicId) || Boolean(highlightedOpeningArgument);

    return (
        // `gap-4` rather than per-child `mb-4`: the composer below is conditional (absent when the
        // discussion is deleted or a nested reply is focused, swapped for the archive banner when it is
        // archived), so spacing hung off it would collapse in exactly those states. The container that
        // owns the rhythm has to be the one that always renders (AGENTS rule 20).
        <div className="mb-20 flex w-full min-w-0 max-w-250 flex-col gap-4">
            {isArchived ? (
                <div data-playwright-id="archived-discussion-thoughts-banner">
                    <InfoBanner
                        title={localizer("discussion.archived.badge")}
                        description={localizer("discussion.archived.banner")}
                    />
                </div>
            ) : isDeleted || isFocusedNestedReply ? null : (
                <div>
                    <ThoughtTextEditor
                        ref={openingComposerRef}
                        localizer={localizer}
                        loggedInUsername={loggedInUsername}
                        isUserLoggedIn={isUserLoggedIn}
                        profileImageUrl={profileImageUrl}
                        openingArgumentPublicId={undefined}
                        conversationPublicId={undefined}
                        discussionPublicId={discussionPublicId!}
                        agreementAgreementTitle={agreementAgreementTitle}
                        disagreementAgreementTitle={disagreementAgreementTitle}
                        playwrightIdPrefix="discussion-opening-thought-editor"
                        hideAvatar={true}
                        roomy={true}
                        showStancePrompts={!isQuestion}
                        publishSurface={publishSurface}
                        // On a question the composer always says so; the fresh + unanswered case
                        // additionally gets the uniqueness cue, reusing `discussion.question.empty.description`
                        // ("Know this one? Your answer could be the one that helps.") — which is exactly the
                        // variant string design §11.1 asked the zero-reply composer to be given, so no key is
                        // minted and the 2026-09-30 exemption boundary is unchanged.
                        paragraphPlaceholderKey={showZeroReplyPrompt && viewerIsDiscussionAuthor
                            ? "replyInvitation.authorZeroReply"
                            : isQuestion
                            ? (showZeroReplyPrompt ? "discussion.question.empty.description" : "discussion.question.answers.write")
                            : (showZeroReplyPrompt ? "replyInvitation.zeroReplyPlaceholder" : undefined)}
                    />
                </div>
            )}
            {listControlsFollowAnchoredCard ? null : listControls}
            {isFocusedNestedReply && openingArgumentPublicId ? (
                <ThoughtAncestorBreadcrumb
                    argumentPublicId={openingArgumentPublicId}
                    discussionSlug={discussionSlug || ""}
                    localizer={localizer}
                    loggedInUsername={loggedInUsername}
                    isSensitive={isSensitive}
                    suppressSpoilerGuards={suppressSpoilerGuards}
                />
            ) : null}
            {highlightedOpeningArgument && (
                <ThoughtInDiscussionCardDisplayer
                    openingArgument={highlightedOpeningArgument}
                    loggedInUsername={loggedInUsername}
                    discussionSlug={discussionSlug || ""}
                    discussionTitle={discussionTitle}
                    agreementAgreementTitle={agreementAgreementTitle}
                    disagreementAgreementTitle={disagreementAgreementTitle}
                    localizer={localizer}
                    discussionPublicId={discussionPublicId}
                    isSensitive={isSensitive}
                    suppressSpoilerGuards={suppressSpoilerGuards}
                    displayMode={"OPENING_HIGHLIGHTED"}
                    replyTreeMaxDepth={3}
                    discussionGroupsByPublicId={discussionGroupsByPublicId}
                    selectedGroupPublicIds={normalizedSelectedGroupPublicIds}
                    onViewConversationClicked={onViewConversationClicked}
                    activeConversationPublicId={activeConversationPublicId}
                    autoOpenReply={autoOpenReply}
                    // The focused card is filtered OUT of the list below, so it carries its own pin
                    // context: the pinned id is its own when it is the pinned thought, which is the only
                    // case where the replace-confirm could matter here.
                    pinnedThoughtPublicId={findPinnedOpeningThought(localItems)?.publicId
                        ?? (highlightedOpeningArgument.openingArgument?.pinned
                            ? highlightedOpeningArgument.openingArgument?.publicId
                            : null)}
                    onPinChanged={handlePinChanged}
                    isQuestion={isQuestion}
                    onAcceptChanged={handleAcceptChanged}
                    discussionAuthorUsername={discussionAuthorUsername}
                />)}
                {/* `DISCUSSION-111` (r41 A46): on a permalink the anchored card is filtered OUT of the
                    list and is never moved or hidden by these controls, so they sit after it, at the
                    head of the list they govern. Above it they read as the card's own header. */}
                {listControlsFollowAnchoredCard ? listControls : null}

                {isFocusedNestedReply ? null : isResolvingFocusedArgument || shouldShowInitialItemsLoading ? (
                    <PaginatedListStatusText>{localizer("home.loading")}</PaginatedListStatusText>
                ) : ssrFallbackHtml ? (
                    // The thoughts could not be loaded, so we re-present the server-rendered list this
                    // island replaced on mount rather than showing an empty discussion. Inert by
                    // design — no voting or replying — but readable and crawlable, which is strictly
                    // better than nothing. `dangerouslySetInnerHTML` is safe here: the markup is our
                    // own JTE output, escaped server-side, captured before mount. See ssrSnapshot.ts.
                    <section
                        data-playwright-id="discussion-thoughts-ssr-fallback"
                        // Rule 32's THIRD door — an island's own terminal-fetch branch — and the marker
                        // it owes. This path renders no `RecoverableIslandFallback` and forwards no
                        // error the client-log oracle can see, and the restored thoughts look EXACTLY
                        // like healthy ones, so none of the island-health oracles could find it: a spec
                        // asserting on the discussion's replies passes in full while the region is inert
                        // and the reader is looking at a pre-mount snapshot. That is the "a recovery
                        // path without a marker has quietly deleted coverage" case rule 32 names. The
                        // `data-playwright-id` above does NOT cover this — it is a spec-targeting hook
                        // that only the two specs naming it ever query. Asserted by
                        // `assertNoIslandFallback` in
                        // `typescript/apps/playwright/tests/helpers/islandHealthGuard.mjs`, which every
                        // lane runs; the attribute name must byte-match
                        // `ISLAND_THOUGHTS_RESTORED_SELECTOR` there, and there is no compile-time link.
                        // Added 2026-08-14.
                        data-island-thoughts-restored="true"
                        dangerouslySetInnerHTML={{__html: ssrFallbackHtml}}
                    />
                ) : (
                    <ThoughtInDiscussionListDisplayer
                        key={`discussion-arguments:${normalizedOpeningArgumentOrdering}:${selectedGroupPublicIdsKey || "all"}`}
                        openingArguments={initialListItems}
                        initialViewerDiscussionAgreement={initialViewerDiscussionAgreement}
                        discussionPublicId={discussionPublicId}
                        loggedInUsername={loggedInUsername}
                        discussionSlug={discussionSlug}
                        discussionTitle={discussionTitle}
                        agreementAgreementTitle={agreementAgreementTitle}
                        disagreementAgreementTitle={disagreementAgreementTitle}
                        localizer={localizer}
                        isSensitive={isSensitive}
                        suppressSpoilerGuards={suppressSpoilerGuards}
                        discussionGroupsByPublicId={discussionGroupsByPublicId}
                        selectedGroupPublicIds={normalizedSelectedGroupPublicIds}
                        openingArgumentOrdering={normalizedOpeningArgumentOrdering}
                        onClearGroupFilter={() => onSelectedGroupPublicIdsChange?.([])}
                        excludeOpeningArgumentPublicIds={excludeOpeningArgumentPublicIds}
                        onWriteArgumentClicked={isReadOnly ? undefined : handleWriteArgumentClicked}
                        onStancePromptChosen={isReadOnly ? undefined : handleStancePromptChosen}
                        onViewConversationClicked={onViewConversationClicked}
                        activeConversationPublicId={activeConversationPublicId}
                        newlyPublishedOpeningArgumentPublicIds={newlyPublishedOpeningArgumentPublicIds}
                        onPinChanged={handlePinChanged}
                        isQuestion={isQuestion}
                        onAcceptChanged={handleAcceptChanged}
                        discussionAuthorUsername={discussionAuthorUsername}
                        viewerLastVisitedAt={viewerLastVisitedAt}
                    />
                )}

        </div>
    );
}
